import { type RefObject, useEffect, useRef } from 'react';
import { releasesSwipe, swipesFrom, useHorizontalDrag } from './use-horizontal-drag';

/** Where a swipe back from the edge starts, from the screen's left edge, in CSS pixels. */
export const EDGE_WIDTH = 24;

/** Whether a released drag goes back: past a third of the screen, or flicked. */
export function releasesBack(input: { dx: number; width: number; velocity: number }): boolean {
  return input.dx > 0 && releasesSwipe(input);
}

/**
 * Drags a pushed screen back under the finger, like a phone's own apps: released far enough
 * (or flicked), it slides away and `onBack` runs; otherwise it returns. It starts from the
 * left edge, or `fromAnywhere` on the screen but sideways content and fields.
 */
export function useSwipeBack(
  ref: RefObject<HTMLElement | null>,
  onBack: () => void,
  enabled: boolean,
  { fromAnywhere = false }: { fromAnywhere?: boolean } = {},
): void {
  const back = useRef(onBack);
  back.current = onBack;
  const settle = (transform: string) => {
    const screen = ref.current;
    if (screen === null) return;
    screen.style.transition = '';
    screen.style.transform = transform;
  };

  useHorizontalDrag(ref, {
    enabled,
    accepts: (event, x) => x <= EDGE_WIDTH || (fromAnywhere && swipesFrom(event.target)),
    allows: (dx) => dx > 0,
    onDrag: (dx) => {
      const screen = ref.current;
      if (screen === null) return;
      screen.style.transition = 'none';
      screen.style.transform = `translateX(${dx}px)`;
    },
    onRelease: (dx, velocity) => {
      const screen = ref.current;
      if (screen === null) return;
      if (!releasesBack({ dx, width: screen.clientWidth, velocity })) {
        settle('');
        return;
      }
      settle('translateX(100%)');
      window.setTimeout(() => {
        screen.style.transform = '';
        back.current();
      }, 220);
    },
    onCancel: () => settle(''),
  });

  useEffect(() => {
    const screen = ref.current;
    if (screen === null || !enabled) return;
    return () => {
      screen.style.transform = '';
      screen.style.transition = '';
    };
  }, [ref, enabled]);
}
